/**
 * Per-event theming helpers.
 *
 * Derives a safe MUI palette override from an event's brand colors, with a
 * contrast guardrail so custom branding can't produce unreadable buttons
 * (white text on a light primary). If a color is too light for white text, we
 * darken it until it meets a minimum contrast ratio (WCAG-ish).
 */

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

function normalizeHex(hex: string): string | null {
  if (!HEX_RE.test(hex)) return null
  let h = hex.slice(1)
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  return `#${h.toLowerCase()}`
}

function toRgb(hex: string): [number, number, number] {
  const h = hex.slice(1)
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

function toHex(rgb: [number, number, number]): string {
  return (
    '#' +
    rgb
      .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
      .join('')
  )
}

function relLuminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

/** Contrast ratio against white. */
function contrastWithWhite(rgb: [number, number, number]): number {
  const l = relLuminance(rgb)
  return 1.05 / (l + 0.05)
}

/** Darken a color toward black until white text has >= target contrast. */
function ensureReadableOnWhiteText(hex: string, target = 3.2): string {
  let rgb = toRgb(hex)
  let guard = 0
  while (contrastWithWhite(rgb) < target && guard < 24) {
    rgb = [rgb[0] * 0.92, rgb[1] * 0.92, rgb[2] * 0.92]
    guard += 1
  }
  return toHex(rgb)
}

function darken(hex: string, amount = 0.2): string {
  const rgb = toRgb(hex).map((v) => v * (1 - amount)) as [number, number, number]
  return toHex(rgb)
}

function lighten(hex: string, amount = 0.2): string {
  const rgb = toRgb(hex).map((v) => v + (255 - v) * amount) as [
    number,
    number,
    number,
  ]
  return toHex(rgb)
}

export interface EventThemeOverride {
  primary: { main: string; light: string; dark: string; contrastText: string }
  secondary?: { main: string; contrastText: string }
}

/**
 * Build a palette override from an event's brand colors. Returns null when no
 * valid custom color is provided (caller keeps the default brand theme).
 */
export function buildEventPalette(
  primaryColor?: string | null,
  accentColor?: string | null,
): EventThemeOverride | null {
  const primary = primaryColor ? normalizeHex(primaryColor) : null
  const accent = accentColor ? normalizeHex(accentColor) : null
  if (!primary && !accent) return null

  const override: EventThemeOverride = {
    primary: {
      main: '#0075BC',
      light: '#5BA9DC',
      dark: '#1C5580',
      contrastText: '#FFFFFF',
    },
  }

  if (primary) {
    const safe = ensureReadableOnWhiteText(primary)
    override.primary = {
      main: safe,
      light: lighten(safe, 0.25),
      dark: darken(safe, 0.2),
      contrastText: '#FFFFFF',
    }
  }
  if (accent) {
    override.secondary = { main: accent, contrastText: '#FFFFFF' }
  }
  return override
}
